import styled from '@emotion/styled';
import H1 from '@base/H1/H1';
import H5 from '@base/H5/H5';
import P2 from '@base/P2/P2';
import Button from '@base/Button/Button';
import { Fonts, mediaQuery, Spacings } from '@styles/index';

import { getSourcesFromUrlMap } from '@utils/resourceHelper';
import pushRoute from '@utils/pushRoute';
import usePlatformGroupAnalytics from '@cont/BatchGroup/usePlatformGroupAnalytics';
import usePlatformGoalBenefits from '@api/hooks/goal/usePlatformGoalBenefits';
import { DEFAULT_QUERY_TYPE } from '@cont/BatchGroup/constants';
import { UPCOMING_SECTION_VIEWED } from '@cont/BatchGroup/constants/events';
import { useIsMobile } from '@stores/AppCommonData';

const Wrapper = styled.div`
  max-width: ${Spacings.APP_CONTAINER_WIDTH};
  margin: auto;
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: ${Spacings.SPACING_38B};
  ${mediaQuery.uptoTablet} {
    max-width: 728px;
  }
  ${mediaQuery.uptoMobile} {
    flex-direction: column-reverse;
    margin: ${Spacings.SPACING_0B};
    padding: ${Spacings.SPACING_0B} ${Spacings.SPACING_4B};
    width: 100%;
  }
`;

const Title = styled(H1)`
  width: 496px;
  min-height: ${Spacings.SPACING_24B};
  font-size: ${Spacings.SPACING_10B};
  line-height: ${Spacings.SPACING_12B};
  margin-bottom: ${Spacings.SPACING_7B};
  ${mediaQuery.uptoTablet} {
    font-size: ${Fonts.H2};
    line-height: ${Fonts.PARA_LINE_HEIGHT};
  }
  ${mediaQuery.uptoMobile} {
    width: 328px;
    min-height: ${Spacings.SPACING_14B};
    font-size: ${Fonts.H3};
    line-height: ${Spacings.SPACING_7B};
    margin: ${Spacings.SPACING_5B} ${Spacings.SPACING_0B};
  }
`;
const Feature = styled.div`
  display: grid;
  grid-template-columns: ${Spacings.SPACING_10B} auto;
  width: 100%;
  grid-column-gap: ${Spacings.SPACING_4B};
  position: relative;
`;

const FeatureIcon = styled.picture`
  grid-column: 1/2;
  grid-row: 1/3;
  img {
    width: ${Spacings.SPACING_10B};
    height: ${Spacings.SPACING_10B};
  }
`;
const FeatureTitle = styled(H5)`
  ${mediaQuery.uptoMobile} {
    font-size: ${Fonts.P1};
    line-height: 18px;
  }
`;
const FeatureSubTitle = styled(P2)`
  color: var(--color-text-secondary);
`;

const FeatureInfo = styled.div`
  display: grid;
  width: 100%;
  grid-template-rows: ${Spacings.SPACING_10B} auto;
  grid-row-gap: ${Spacings.SPACING_5B};
  margin-bottom: ${Spacings.SPACING_10B};
  ${mediaQuery.uptoMobile} {
    margin: ${Spacings.SPACING_7B} ${Spacings.SPACING_0B};
    grid-row-gap: ${Spacings.SPACING_6B};
  }
`;
const Left = styled.div`
  width: 496px;
  ${mediaQuery.uptoMobile} {
    width: 100%;
    padding: ${Spacings.SPACING_0B} ${Spacings.SPACING_0B};
  }
`;
const ViewButton = styled(Button)`
  width: fit-content;
  height: auto;
  font-size: ${Fonts.H5};
  line-height: ${Fonts.PARA_LINE_HEIGHT};
  padding: ${Spacings.SPACING_4B} ${Spacings.SPACING_11B};
  display: flex;
  flex-direction: column;
  align-items: center;
  border-radius: ${Spacings.SPACING_1B};
  ${mediaQuery.uptoMobile} {
    font-size: ${Fonts.H5};
    padding: ${Spacings.SPACING_4B} ${Spacings.SPACING_0B};
    line-height: ${Spacings.SPACING_4B};
    width: 100%;
    margin: ${Spacings.SPACING_0B} auto;
  }
`;
const Right = styled.div``;
const GoalImage = styled.picture`
  img {
    width: 100%;
    max-height: 380px;
  }
  ${mediaQuery.uptoMobile} {
    img {
      width: 100%;
      border-radius: unset;
    }
  }
`;

const PlatformHeader = ({ goalUID, goalSlug }) => {
  const { platformGoalBenefits: content } = usePlatformGoalBenefits(goalUID);
  const sendAnalyticsToSegment = usePlatformGroupAnalytics(goalUID);
  const isMobile = useIsMobile();

  if (!content) return null;
  const { mWebImageUrl, webImageUrl } = content;
  const imageUrl = isMobile ? mWebImageUrl : webImageUrl;
  const handleClick = () => {
    const platformBatchesSection = document.getElementsByClassName(
      'platform-batches-sections'
    );
    const hasSectionOnGoalPage =
      !(platformBatchesSection?.length === 0) ?? true;
    sendAnalyticsToSegment(UPCOMING_SECTION_VIEWED, {
      last_primary_source_section: 'Platform Home - Platform Goal Header',
      is_empty_state: hasSectionOnGoalPage,
      batch_type: 'All batches'
    });
    pushRoute(
      `/goal/[goalSlug]/[goalUID]/upcoming-batches?type=${DEFAULT_QUERY_TYPE}`,
      `/goal/${goalSlug}/${goalUID}/upcoming-batches?type=${DEFAULT_QUERY_TYPE}`,
      {},
      'goal'
    );
  };
  return (
    <>
      <Wrapper>
        <Left>
          <Title>{content.title}</Title>
          <FeatureInfo>
            {(content.features || []).map((feature) => (
              <Feature key={feature.title}>
                <FeatureIcon>
                  {getSourcesFromUrlMap(feature.urls, true)}
                </FeatureIcon>
                <FeatureTitle>{feature.title}</FeatureTitle>
                <FeatureSubTitle>{feature.subTitle}</FeatureSubTitle>
              </Feature>
            ))}
          </FeatureInfo>
          <ViewButton
            onClick={handleClick}
            label={content?.cta?.label ?? 'Choose a batch'}
            variant="filled"
          />
        </Left>
        <Right>
          <GoalImage>{getSourcesFromUrlMap(imageUrl, true)}</GoalImage>
        </Right>
      </Wrapper>
    </>
  );
};

export default PlatformHeader;
